// src/features/properties/PropertyCard.jsx
import React, { useState } from 'react';
import { IoBedOutline } from 'react-icons/io5';
import { LuBath, LuRuler } from 'react-icons/lu';
import { HiOutlineLocationMarker, HiHeart, HiOutlineHeart } from 'react-icons/hi';
import { FiMessageSquare, FiBarChart2, FiShare2, FiBookOpen, FiUsers } from 'react-icons/fi';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { startConversation, toggleFavorite } from '../../services/api';
import { useCompare } from '../../context/CompareContext';
import { API_URL } from '../../services/api';

const PropertyCard = ({ propiedad, isFav = false, priority = false }) => {
    const [isFavorite, setIsFavorite] = useState(isFav);
    const [loadingFav, setLoadingFav] = useState(false);
    const [loadingContact, setLoadingContact] = useState(false);
    const { titulo, precio, ciudad, direccion, tipo, habitaciones, banos, area_m2, imagen_url, para_estudiantes, propietario_id, id } = propiedad;

    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const { addToCompare, removeFromCompare, isComparing, comparingList } = useCompare();

    const comparing = isComparing(id);
    const atMax = comparingList.length >= 3;

    const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(p);

    const tipoLabel = {
        casa: 'Casa', apartamento: 'Apto', habitacion: 'Habitación', estudio: 'Estudio',
        local: 'Local', oficina: 'Oficina', bodega: 'Bodega', consultorio: 'Consultorio',
        lote: 'Lote', finca: 'Finca',
    };
    const tipoColor = {
        casa: 'bg-emerald-500', apartamento: 'bg-brand-500', habitacion: 'bg-amber-500',
        estudio: 'bg-pink-500', local: 'bg-violet-500', oficina: 'bg-indigo-400',
        bodega: 'bg-stone-500', consultorio: 'bg-teal-500', lote: 'bg-lime-600', finca: 'bg-green-700',
    };

    const imgUrl = imagen_url
        ? (imagen_url.startsWith('/') ? `${API_URL}${imagen_url}` : imagen_url)
        : null;

    const handleCompare = (e) => {
        e.preventDefault(); e.stopPropagation();
        comparing ? removeFromCompare(id) : (!atMax && addToCompare(propiedad));
    };

    const handleContact = async () => {
        if (!isAuthenticated) return navigate('/login');
        if (String(user.id) === String(propietario_id)) return navigate('/my-properties');
        setLoadingContact(true);
        try {
            const data = await startConversation(id, user.id, propietario_id);
            navigate('/messages', { state: { openConversationId: data.id } });
        } catch (e) { console.error(e); }
        finally { setLoadingContact(false); }
    };

    return (
        <div className="bg-white dark:bg-surface-850 rounded-2xl border border-surface-100 dark:border-surface-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden group animate-fade-in-up flex flex-col">

            {/* ── Image ── */}
            <Link to={`/property/${id}`} className="block relative overflow-hidden" style={{ paddingTop: '62%' }}>
                <img
                    src={imgUrl || '/logoHoommy.png'}
                    alt={`${titulo} en ${ciudad}`}
                    className="absolute inset-0 object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                    loading={priority ? 'eager' : 'lazy'}
                    fetchpriority={priority ? 'high' : 'auto'}
                    decoding={priority ? 'sync' : 'async'}
                />
                {/* Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                {/* Top-left badges: tipo, estudiantes, familiar */}
                <div className="absolute top-3 left-3 flex gap-1.5">
                    <span className={`${tipoColor[tipo] || 'bg-brand-500'} text-white text-[11px] font-bold px-2.5 py-1 rounded-lg uppercase tracking-wide shadow`}>
                        {tipoLabel[tipo] || tipo}
                    </span>

                    {para_estudiantes && (
                        <span className="bg-emerald-500 text-white text-[11px] font-bold px-2 py-1 rounded-lg shadow flex items-center gap-1">
                            <FiBookOpen className="w-3 h-3" /> Est.
                        </span>
                    )}

                    {propiedad.ambiente_familiar && (
                        <span className="bg-amber-500 text-white text-[11px] font-bold px-2 py-1 rounded-lg shadow flex items-center gap-1">
                            <FiUsers className="w-3 h-3" /> Fam.
                        </span>
                    )}
                </div>

                {/* Share */}
                <button
                    onClick={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        const url = `${window.location.origin}/property/${id}`;
                        if (navigator.share) { navigator.share({ title: titulo, url }); }
                        else { navigator.clipboard.writeText(url); }
                    }}
                    className="absolute top-3 right-12 p-2 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-all active:scale-95 duration-100 cursor-pointer"
                    aria-label="Compartir"
                >
                    <FiShare2 className="text-white w-3.5 h-3.5" />
                </button>

                {/* Favorite */}
                <button
                    onClick={async (e) => {
                        e.preventDefault(); e.stopPropagation();
                        if (!isAuthenticated) return navigate('/login');
                        if (loadingFav) return;
                        setLoadingFav(true);
                        try {
                            const res = await toggleFavorite(user.id, id);
                            setIsFavorite(res.favorited);
                        } catch (err) { console.error(err); }
                        finally { setLoadingFav(false); }
                    }}
                    disabled={loadingFav}
                    className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-sm transition-all active:scale-95 duration-100 cursor-pointer ${isFavorite ? 'bg-rose-500/90 hover:bg-rose-500' : 'bg-black/30 hover:bg-black/50'} ${loadingFav ? 'opacity-50' : ''}`}
                    aria-label="Favorito"
                >
                    {isFavorite
                        ? <HiHeart className="text-white w-3.5 h-3.5" />
                        : <HiOutlineHeart className="text-white w-3.5 h-3.5" />
                    }
                </button>

                {/* Price on image */}
                <div className="absolute bottom-0 left-0 right-0 px-4 py-3 transition-all duration-300">
                    <span className="text-white text-lg font-bold drop-shadow-lg">{formatPrice(precio)}</span>
                    <span className="text-white/70 text-xs ml-1">/mes</span>
                </div>
            </Link>

            {/* ── Info ── */}
            <div className="p-4 flex flex-col flex-1">
                <Link to={`/property/${id}`}>
                    <h3 className="text-surface-900 dark:text-white font-semibold text-sm leading-snug line-clamp-1 hover:text-brand-600 transition-colors">
                        {titulo}
                    </h3>
                </Link>
                <div className="flex items-center text-surface-500 dark:text-surface-400 text-xs mt-1.5">
                    <HiOutlineLocationMarker className="mr-1 text-brand-500 flex-shrink-0" />
                    <span className="line-clamp-1">{ciudad}{direccion ? ` — ${direccion}` : ''}</span>
                </div>

                {/* Specs */}
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-surface-100 dark:border-surface-800">
                    <div className="flex items-center text-surface-500 dark:text-surface-400 text-xs gap-1">
                        <IoBedOutline className="text-brand-500 w-3.5 h-3.5" />
                        <span>{habitaciones} hab.</span>
                    </div>
                    <div className="flex items-center text-surface-500 dark:text-surface-400 text-xs gap-1">
                        <LuBath className="text-brand-500 w-3.5 h-3.5" />
                        <span>{banos} baño{banos !== 1 ? 's' : ''}</span>
                    </div>
                    {area_m2 && (
                        <div className="flex items-center text-surface-500 dark:text-surface-400 text-xs gap-1">
                            <LuRuler className="text-brand-500 w-3.5 h-3.5" />
                            <span>{area_m2} m²</span>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="mt-auto flex gap-2">
                    <Link to={`/property/${id}`} className="flex-1 btn-primary py-2 text-xs text-center rounded-xl uppercase tracking-wide active:scale-95 transition-transform duration-100 cursor-pointer">
                        Ver detalles
                    </Link>
                    <button
                        onClick={handleCompare}
                        disabled={atMax && !comparing}
                        title={comparing ? 'Quitar del comparador' : atMax ? 'Máx. 3' : 'Comparar'}
                        className={`flex items-center justify-center px-3 py-2 text-sm rounded-xl border transition-colors active:scale-95 duration-100 cursor-pointer ${comparing
                            ? 'bg-brand-50 border-brand-400 text-brand-700 dark:bg-brand-900/30 dark:border-brand-700 dark:text-brand-400'
                            : atMax
                                ? 'border-surface-200 text-surface-300 dark:border-surface-700 cursor-not-allowed'
                                : 'border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800'
                            }`}
                    >
                        <FiBarChart2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={handleContact}
                        disabled={loadingContact}
                        title="Contactar propietario"
                        className={`flex items-center justify-center px-3 py-2 text-sm rounded-xl border border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors active:scale-95 duration-100 cursor-pointer ${loadingContact ? 'opacity-50 cursor-wait' : ''}`}
                    >
                        <FiMessageSquare className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PropertyCard;
